'use client'

/*
 * TestPromptPopup
 *
 * A timed prompt inviting the reader to take the test. Built for the percentile
 * calculator, but it takes everything it needs as props so it can go on other
 * pages later.
 *
 * THREE DELIBERATE CHOICES, EACH WITH A REASON:
 *
 * 1. It is a bottom sheet on mobile and a centred modal only from the `sm`
 *    breakpoint up. Google's intrusive interstitial guidance is explicitly
 *    about mobile search results, and it singles out popups that cover the main
 *    content while someone is reading. A sheet pinned to the bottom edge uses a
 *    "reasonable amount of screen space", which is the wording that guidance
 *    uses for banners it considers fine. The calculator is a page we want to
 *    rank, so it is not worth trading rankings for a slightly larger modal.
 *
 * 2. Nothing renders until the timer fires. `open` starts false, so the server
 *    renders no dialog markup at all and a crawler that does not execute
 *    JavaScript sees a page with no popup in it whatsoever.
 *
 * 3. The delay is plain wall-clock time, and if it elapses while the tab is
 *    hidden the prompt waits until the tab is looked at again rather than
 *    opening behind the reader's back. An earlier version instead counted only
 *    visible time, which meant loading the page and switching windows paused
 *    the countdown and made the prompt look broken.
 *
 * The page stays scrollable while the prompt is open. Locking body scroll is
 * what turns a prompt into an obstruction, and it is the thing most likely to
 * make someone bounce.
 *
 * IT RENDERS THROUGH A PORTAL. The component is mounted inside the calculator
 * so it can read the score the reader is looking at, and the calculator's root
 * card carries `overflow-hidden` for its rounded corners. A position:fixed
 * element only escapes an ancestor's overflow clipping while no ancestor
 * establishes a containing block, and any `transform`, `filter`, `backdrop-
 * filter`, `perspective`, `contain` or `will-change` added to that card later
 * would silently start clipping this dialog. Portalling to document.body means
 * the dialog can never be clipped or trapped in a stacking context, whatever
 * happens to the markup around it.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { X, ArrowRight, Brain } from 'lucide-react'

export default function TestPromptPopup({
  delayMs = 10000,
  score = null,
  percentileLabel = null,
  headline = null,
  body = null,
  storageKey = 'biqt_test_prompt_dismissed',
}) {
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  const [suppressed, setSuppressed] = useState(false)

  const dialogRef = useRef(null)
  const closeButtonRef = useRef(null)
  const previouslyFocused = useRef(null)

  // sessionStorage rather than localStorage: dismissing it should last for the
  // visit, not forever. Every accessor is wrapped because Safari private mode
  // throws on access rather than returning null.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(storageKey) === '1') setSuppressed(true)
    } catch {
      // Storage unavailable. The prompt still works, it just will not remember
      // a dismissal across page views in this session.
    }
  }, [storageKey])

  // Development-only escape hatch: /iq-percentile-calculator?prompt=now opens
  // the prompt immediately and ignores a stored dismissal. Without this the
  // only way to check a change is to sit on the page for ten seconds and clear
  // sessionStorage between attempts. Gated on NODE_ENV so it cannot be
  // triggered in production.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return
    if (new URLSearchParams(window.location.search).get('prompt') === 'now') {
      setSuppressed(false)
      setOpen(true)
    }
  }, [])

  // A plain wall-clock timer. An earlier version accumulated only the time the
  // tab was actually visible, which sounded better and behaved worse: loading
  // the page and then switching to another window paused the countdown, so the
  // prompt appeared not to work at all. Predictable beats clever here.
  //
  // The no-ambush property is kept a simpler way. If the timer happens to
  // elapse while the tab is hidden, the prompt waits for the next time the tab
  // is actually looked at instead of opening behind the reader's back.
  useEffect(() => {
    if (suppressed || open) return

    let cancelled = false
    let removeVisibilityListener = null

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        removeVisibilityListener?.()
        if (!cancelled) setOpen(true)
      }
    }

    const timer = setTimeout(() => {
      if (cancelled) return

      if (document.visibilityState === 'visible') {
        setOpen(true)
        return
      }

      document.addEventListener('visibilitychange', onVisibilityChange)
      removeVisibilityListener = () =>
        document.removeEventListener('visibilitychange', onVisibilityChange)
    }, delayMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
      removeVisibilityListener?.()
    }
  }, [delayMs, suppressed, open])

  // Split from `open` so the element is in the DOM for one frame before the
  // transition classes change, otherwise it snaps into place with no animation.
  useEffect(() => {
    if (!open) return

    previouslyFocused.current = document.activeElement
    const raf = requestAnimationFrame(() => setVisible(true))
    const focusTimer = setTimeout(() => closeButtonRef.current?.focus(), 80)

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'test_prompt_shown', {
        event_category: 'engagement',
        event_label: score != null ? `score_${score}` : 'no_score',
      })
    }

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(focusTimer)
    }
    // `score` is deliberately not a dependency. It changes on every slider
    // move, and including it would re-run this effect while the dialog is
    // already open: re-firing the analytics event and yanking focus back to
    // the close button each time. The value is only read once, when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const dismiss = useCallback((reason) => {
    setVisible(false)
    try {
      window.sessionStorage.setItem(storageKey, '1')
    } catch {
      // Nothing to do. Worst case it reappears on the next page view.
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'test_prompt_dismissed', {
        event_category: 'engagement',
        event_label: reason,
      })
    }

    // Matches the 300ms leave transition so the node is removed after it fades.
    setTimeout(() => {
      setOpen(false)
      setSuppressed(true)
      previouslyFocused.current?.focus?.()
    }, 300)
  }, [storageKey])

  // Escape to close, and a minimal focus trap. Without the trap, tabbing out of
  // an open dialog lands on the page behind it, which is disorienting for
  // keyboard and screen reader users.
  useEffect(() => {
    if (!open) return

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        dismiss('escape')
        return
      }

      if (e.key !== 'Tab' || !dialogRef.current) return

      const focusable = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, dismiss])

  const handleAccept = () => {
    try {
      window.sessionStorage.setItem(storageKey, '1')
    } catch {
      // Non-fatal, see above.
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'test_prompt_accepted', {
        event_category: 'conversion',
        event_label: score != null ? `score_${score}` : 'no_score',
      })
    }
  }

  // Guarded because document does not exist during server rendering. `open`
  // can only become true from an effect, so this is really belt and braces.
  if (!open || typeof document === 'undefined') return null

  // The prompt reads very differently depending on whether someone has actually
  // used the calculator. If they have, it can refer to the number they were
  // just looking at, which makes it a continuation of what they were doing
  // rather than an advert interrupting it.
  const hasScore = score != null && percentileLabel != null

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="test-prompt-heading"
    >
      {/* Backdrop is desktop only. On mobile the sheet sits over the bottom
          edge and the content above it stays visible and readable. */}
      <div
        aria-hidden="true"
        onClick={() => dismiss('backdrop')}
        className={`hidden sm:block absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] pointer-events-auto transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        ref={dialogRef}
        className={`relative pointer-events-auto w-full sm:max-w-md bg-white shadow-2xl border border-gray-200 rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 m-0 sm:m-6 transition-all duration-300 ease-out ${
          visible ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-6 opacity-0 sm:scale-95'
        }`}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={() => dismiss('close_button')}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md mb-4">
          <Brain className="w-6 h-6 text-white" />
        </div>

        <h2 id="test-prompt-heading" className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-2">
          {headline || (hasScore ? 'Is that your number, or a guess?' : 'Curious where you actually land?')}
        </h2>

        <p className="text-gray-600 leading-relaxed mb-6">
          {body || (hasScore ? (
            <>
              You have been looking at <strong>IQ {score}</strong>, the{' '}
              <strong>{percentileLabel}th percentile</strong>. If that figure came
              from somewhere you are not sure about, our 50-question assessment
              measures it properly across verbal, numerical, spatial and memory
              reasoning.
            </>
          ) : (
            <>
              Our 50-question assessment measures verbal, numerical, spatial and
              memory reasoning, and gives you a full-scale score with your exact
              percentile and a breakdown of where you are strongest.
            </>
          ))}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            href="/test"
            onClick={handleAccept}
            className="flex-1 inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl transition-all shadow-md hover:shadow-lg"
          >
            Take the test
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
          <button
            type="button"
            onClick={() => dismiss('no_thanks')}
            className="sm:flex-none inline-flex items-center justify-center text-gray-600 hover:text-gray-900 font-medium px-5 py-3 rounded-xl hover:bg-gray-100 transition-colors"
          >
            Not now
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center sm:text-left">
          50 questions · Full cognitive breakdown · 2.5M+ completed
        </p>
      </div>
    </div>,
    document.body,
  )
}
